import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Chip, Typography } from "@mui/material";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import {
    appDataGridSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../Helpers/dataGridStyles.js";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import "../Products/catalog.css";

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function money(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return currencyFormatter(n, 2);
}

function dash(value) {
    return value == null || value === "" ? "—" : value;
}

function statusChipSx(status) {
    const key = String(status || "").toLowerCase();
    const base = {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
    };
    if (key.includes("cancel")) {
        return { ...base, backgroundColor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
    }
    if (/(fulfilled|shipped|delivered)/.test(key)) {
        return { ...base, backgroundColor: "#ecfdf5", color: "#047857", border: "1px solid #6ee7b7" };
    }
    if (/(not started|created|pending)/.test(key)) {
        return { ...base, backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74" };
    }
    if (/(in progress|acknowledged|requested)/.test(key)) {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    if (key === "ebay") {
        return {
            height: 24,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#fef3c7",
            color: "#854d0e",
            border: "1px solid #fcd34d",
        };
    }
    if (key === "walmart") {
        return {
            height: 24,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            border: "1px solid #93c5fd",
        };
    }
    return {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#f1f5f9",
        color: "#475569",
    };
}

export default function AllOrdersGrid({ orders, loading }) {
    const rows = useMemo(
        () =>
            (orders || []).map((order) => {
                const f = order.fields || {};
                return {
                    id: order.id,
                    orderNumber: f["Order Number"] || f["Walmart Customer Order ID"] || "",
                    channel: f["Selling channel"] || "",
                    status: f["Order Status"] || "",
                    platformStatus: f["Platform Status"] || "",
                    orderDate: formatDate(f["Order Creation Date"]),
                    itemId: f["Item ID"] || f["SKU Helper"] || "",
                    itemTitle: f["Item Title"] || "",
                    buyerName: f["Buyer Name"] || "",
                    city: f["Ship to city"] || "",
                    state: f["Ship to province/region/state"] || "",
                    zip: f["Ship to zip"] || "",
                    quantity: f["Quantity"] ?? "",
                    assignedUnits: f["Assigned Units"] ?? 0,
                    catalogItemIds: f["Catalog Item IDs"] || "",
                    itemPrice: f["Item price"],
                    gross: f["Gross amount"],
                    shippingService: f["Shipping Service"] || "",
                    tracking: f["Shipping Tracking Number"] || "",
                };
            }),
        [orders]
    );

    const layout = useGridLayout(GRID_LAYOUT_KEYS.orders, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = useMemo(
        () => [
            { field: "orderNumber", headerName: "Order Number", minWidth: 170, flex: 0.8 },
            {
                field: "channel",
                headerName: "Selling channel",
                minWidth: 130,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) return dash(text);
                    return (
                        <Chip
                            size="small"
                            label={text}
                            sx={channelChipSx(text)}
                        />
                    );
                },
            },
            {
                field: "status",
                headerName: "Order Status",
                minWidth: 150,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Chip
                            size="small"
                            label={text}
                            sx={statusChipSx(text)}
                        />
                    );
                },
            },
            {
                field: "platformStatus",
                headerName: "Platform Status",
                minWidth: 160,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return <Chip size="small" label={text} sx={statusChipSx(text)} />;
                },
            },
            { field: "orderDate", headerName: "Order Creation Date", minWidth: 150 },
            { field: "itemId", headerName: "Item ID", minWidth: 140 },
            { field: "itemTitle", headerName: "Item Title", minWidth: 240, flex: 1.4 },
            { field: "buyerName", headerName: "Buyer Name", minWidth: 150 },
            { field: "city", headerName: "Ship to city", minWidth: 130 },
            { field: "state", headerName: "State", minWidth: 90 },
            { field: "zip", headerName: "Zip", width: 90 },
            { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
            {
                field: "catalogItemIds",
                headerName: "Assigned Item",
                minWidth: 140,
                flex: 0.6,
                valueGetter: (_value, row) => row.catalogItemIds || "—",
            },
            {
                field: "itemPrice",
                headerName: "Item price",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => money(value) || "—",
            },
            {
                field: "gross",
                headerName: "Gross amount",
                width: 120,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => money(value) || "—",
            },
            { field: "shippingService", headerName: "Shipping Service", minWidth: 150 },
            { field: "tracking", headerName: "Tracking Number", minWidth: 160 },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    return (
        <div className="catalog-grid-shell">
            <div className="catalog-toolbar">
                <div className="catalog-toolbar-spacer" />
                <GridLayoutControls layout={layout} columns={baseColumns} />
            </div>
            <div className="catalog-grid-main">
                <DataGrid
                    {...appDataGridDefaultProps}
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    sortModel={layout.sortModel}
                    onSortModelChange={layout.onSortModelChange}
                    columnVisibilityModel={layout.columnVisibilityModel}
                    onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                    onColumnWidthChange={layout.onColumnWidthChange}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    slotProps={appDataGridSlotProps}
                    sx={appDataGridSx}
                />
            </div>
        </div>
    );
}
